import { call, put, select } from 'typed-redux-saga'
import { AnyAction } from 'redux'

import { ApplicationState } from '@store/types'
import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { TipoEmprestimo } from '@utils/enums'
import services from '@services'
import { getParcelaAtualSimulacoes, logout } from '@utils/functions'
import { BridgeService } from '@services/bridge'
import { HttpStatus } from '@services/base'
import { ErroType } from '@utils/types'
import { SimulacaoResponse } from '@services/getSimulacaoRefinanciamentoService'

import { InfoParcelas, pendencyType, SimulacaoRequestPayload } from '../types'
import {
  selectSimulacao,
  setParcelaSelecionada,
  setSaldoDevedor,
  setTotalParcelas,
  simulacaoError,
  simulacaoSuccess,
} from '../actions'

const hasSimulations = (simulations?: InfoParcelas[]) => Boolean(simulations?.length)

const shouldSelectSemSeguro = (data: SimulacaoResponse) =>
  hasSimulations(data?.simulations) &&
  (!data?.simulationsWithInsurance || data.simulationsWithInsurance.length === 0)

const shouldSelectComSeguro = (data: SimulacaoResponse) =>
  (!data?.simulations || data.simulations.length === 0) &&
  hasSimulations(data?.simulationsWithInsurance)

function* selectParcelaPorTipo(simulations: InfoParcelas[], tipo: TipoEmprestimo) {
  const parcelaAtual = getParcelaAtualSimulacoes(simulations)
  yield put(setParcelaSelecionada(parcelaAtual))
  yield put(setTotalParcelas(parcelaAtual.numberOfInstallments))
  yield put(selectSimulacao(tipo))
}

function* applySimulacaoSelection(data: SimulacaoResponse) {
  if (shouldSelectSemSeguro(data)) {
    yield* selectParcelaPorTipo(data.simulations, TipoEmprestimo.SEM_CREDITO_PROTEGIDO)
    return
  }

  if (shouldSelectComSeguro(data)) {
    yield* selectParcelaPorTipo(data.simulationsWithInsurance, TipoEmprestimo.CREDITO_PROTEGIDO)
  }
}

function* handleSimulacaoUnauthorized() {
  yield put(
    showError({
      title: 'Sessão expirada',
      message: 'Efetue o login novamente.',
      actionTitle: 'Continuar',
      functionCallback: () => logout(),
    }),
  )
}

export default function* getSimulacaoSaga(action: AnyAction) {
  const payload = action.payload as SimulacaoRequestPayload
  const { accessToken } = yield* select((state: ApplicationState) => state.session)

  try {
    const { data } = yield* call(services.getSimulacaoRefinanciamentoService, accessToken)
    yield put(
      setLastServiceResponse({
        code: data?.code ?? 'OK',
        serviceName: 'getSimulacaoRefinanciamentoService',
      }),
    )

    if (BridgeService.isBrowser() && data?.code) {
      data.pending = data.code as pendencyType
    }

    yield put(setSaldoDevedor(data?.debtBalance))
    yield put(simulacaoSuccess(data))

    yield* applySimulacaoSelection(data)

    yield payload.callback({
      pending: data.pending,
      simulations: data?.simulations ?? [],
      simulationsWithInsurance: data?.simulationsWithInsurance ?? [],
    })
  } catch (error) {
    yield put(
      setLastServiceResponse({ code: 'ERROR', serviceName: 'getSimulacaoRefinanciamentoService' }),
    )
    const erroTyped = error as ErroType
    yield put(simulacaoError())

    if (BridgeService.isBrowser() && erroTyped.status === HttpStatus.UNAUTHORIZED) {
      yield* handleSimulacaoUnauthorized()
      return
    }

    payload.erroCallback()
  }
}
